import { render } from 'preact';
import qrcode from 'qrcode-generator';
import { buildPdf, type PdfImage } from '../../shared/pdf.js';
import { t } from '../i18n/index.js';
import { buildZip } from '../../shared/zip.js';
import { CARDS_PER_PAGE, cardSite, cardTexts, cardUrl, cardValidText, PrintLogo, sloganFor, spaced, type InviteCard } from './InviteCards.js';

// Saving invite cards as files (decision 2026-09-29): PDF for printing (A4, 8 cards per sheet,
// same layout as the printout) and JPG for sharing by e-mail or WhatsApp. The cards are drawn on a
// canvas at 300 dpi with the measures of the printed card (styles.css, @media print).

/** txt-zip: bingo codes as text files with instructions, all in one ZIP (user request 2026-09-30). */
export type SaveFormat = 'pdf' | 'jpg-sheets' | 'jpg-cards' | 'txt-zip';

const DPI = 300;
const PX = DPI / 25.4; // pixels per millimetre
const px = (mm: number) => mm * PX;
const PT = 25.4 / 72; // millimetres per point
const CARD_W = 95;
const CARD_H = 69;
const PAGE_MARGIN = 10;
/** White border around a JPG sheet. */
const JPG_MARGIN = 4;
const SANS = 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';
const MONO = 'ui-monospace, "Cascadia Mono", Consolas, monospace';

let logoPromise: Promise<HTMLImageElement> | null = null;

/** The printed colour logo as an image (the same SVG as on paper). */
function logoImage(): Promise<HTMLImageElement> {
  logoPromise ??= new Promise((resolve, reject) => {
    const host = document.createElement('div');
    render(<PrintLogo />, host);
    const svg = host.querySelector('svg')!.cloneNode(true) as SVGSVGElement;
    render(null, host);
    svg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
    svg.setAttribute('width', String(Math.round(px(42))));
    svg.setAttribute('height', String(Math.round((px(42) * 48) / 230)));
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => {
      logoPromise = null;
      reject(new Error('logo'));
    };
    img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(new XMLSerializer().serializeToString(svg))}`;
  });
  return logoPromise;
}

function setFont(ctx: CanvasRenderingContext2D, sizeMm: number, style = '', family = SANS) {
  ctx.font = `${style} ${px(sizeMm)}px ${family}`.trim();
}

/** Letter spacing in em (ignored by browsers without canvas letter spacing). */
function setSpacing(ctx: CanvasRenderingContext2D, em: number, sizeMm: number) {
  (ctx as CanvasRenderingContext2D & { letterSpacing?: string }).letterSpacing = `${px(em * sizeMm)}px`;
}

function wrap(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  const lines: string[] = [];
  let line = '';
  for (const word of text.split(' ')) {
    const next = line ? `${line} ${word}` : word;
    if (line && ctx.measureText(next).width > maxWidth) {
      lines.push(line);
      line = word;
    } else {
      line = next;
    }
  }
  if (line) lines.push(line);
  return lines;
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function drawQr(ctx: CanvasRenderingContext2D, text: string, x: number, y: number, size: number) {
  const qr = qrcode(0, 'M');
  qr.addData(text);
  qr.make();
  const n = qr.getModuleCount();
  const module = size / (n + 8); // 4-module quiet zone on every side
  ctx.fillStyle = '#fff';
  ctx.fillRect(px(x), px(y), px(size), px(size));
  ctx.fillStyle = '#0d1220';
  for (let row = 0; row < n; row++) {
    for (let col = 0; col < n; col++) {
      // Rounded to whole pixels (and slightly overlapping) so that no seams appear between modules.
      if (!qr.isDark(row, col)) continue;
      const left = Math.floor(px(x + (col + 4) * module));
      const top = Math.floor(px(y + (row + 4) * module));
      ctx.fillRect(left, top, Math.ceil(px(module)) + 1, Math.ceil(px(module)) + 1);
    }
  }
}

interface CardStyle {
  withQr: boolean;
  /** Dashed cut line (sheets) or a thin frame (single card). */
  frame: 'cut' | 'thin';
}

/** Draws one card with its top left corner at (ox, oy) millimetres. */
function drawCard(ctx: CanvasRenderingContext2D, logo: HTMLImageElement, card: InviteCard, index: number, ox: number, oy: number, style: CardStyle) {
  ctx.save();
  const texts = cardTexts(card.kind);
  ctx.fillStyle = '#fff';
  ctx.fillRect(px(ox), px(oy), px(CARD_W), px(CARD_H));

  const stripe = ctx.createLinearGradient(0, px(oy), 0, px(oy + CARD_H));
  stripe.addColorStop(0, '#1fa8ea');
  stripe.addColorStop(0.6, '#7c5cf0');
  stripe.addColorStop(1, '#d946ef');
  ctx.fillStyle = stripe;
  ctx.fillRect(px(ox), px(oy), px(3), px(CARD_H));

  if (style.frame === 'cut') {
    ctx.strokeStyle = '#9aa3b5';
    ctx.lineWidth = px(0.3);
    ctx.setLineDash([px(1.2), px(0.9)]);
  } else {
    ctx.strokeStyle = '#d5dae4';
    ctx.lineWidth = px(0.4);
  }
  ctx.strokeRect(px(ox + 0.15), px(oy + 0.15), px(CARD_W - 0.3), px(CARD_H - 0.3));
  ctx.setLineDash([]);

  const left = ox + 7;
  const right = style.withQr ? ox + CARD_W - 5 - 27 - 3 : ox + CARD_W - 5;
  const width = px(right - left);
  ctx.textBaseline = 'top';
  ctx.textAlign = 'left';

  // Top: logo, heading and slogan.
  const logoW = style.withQr ? 38 : 42;
  const logoH = (logoW * 48) / 230;
  ctx.drawImage(logo, px(left), px(oy + 5), px(logoW), px(logoH));
  let y = oy + 5 + logoH + 1;
  const headingSize = 9 * PT;
  setFont(ctx, headingSize, '700');
  setSpacing(ctx, 0.12, headingSize);
  ctx.fillStyle = '#7c5cf0';
  ctx.fillText(texts.heading.toUpperCase(), px(left), px(y));
  y += headingSize * 1.2 + 0.5;
  const sloganSize = 9.5 * PT;
  setFont(ctx, sloganSize, 'italic');
  setSpacing(ctx, 0, sloganSize);
  ctx.fillStyle = '#34405c';
  for (const line of wrap(ctx, sloganFor(index, card.kind), width)) {
    ctx.fillText(line, px(left), px(y));
    y += sloganSize * 1.25;
  }

  // Bottom up: validity, where to register, the code.
  // The line must not run under the QR code (the bingo text is long): smaller font if needed.
  let validSize = 7.5 * PT;
  setFont(ctx, validSize);
  while (ctx.measureText(cardValidText(card)).width > width && validSize > 5 * PT) setFont(ctx, (validSize -= 0.1));
  const validTop = oy + CARD_H - 4 - validSize * 1.2;
  ctx.fillStyle = '#4b5874';
  ctx.fillText(cardValidText(card), px(left), px(validTop));

  let registerSize = 8 * PT;
  const site = cardSite(card.kind);
  const first = `${texts.register} `;
  const measureRegister = () => {
    setFont(ctx, registerSize);
    const a = ctx.measureText(first).width;
    setFont(ctx, registerSize, '700');
    return a + ctx.measureText(site).width;
  };
  while (measureRegister() > width && registerSize > 5 * PT) registerSize -= 0.1;
  const lineH = registerSize * 1.3;
  const lines = style.withQr ? 2 : 1;
  const registerTop = validTop - 0.8 - lines * lineH;
  setFont(ctx, registerSize);
  ctx.fillStyle = '#16213a';
  ctx.fillText(first, px(left), px(registerTop));
  const siteX = px(left) + ctx.measureText(first).width;
  setFont(ctx, registerSize, '700');
  ctx.fillStyle = '#1f7fd0';
  ctx.fillText(site, siteX, px(registerTop));
  if (style.withQr) {
    setFont(ctx, registerSize);
    ctx.fillStyle = '#4b5874';
    ctx.fillText(texts.scan, px(left), px(registerTop + lineH));
  }

  const labelSize = 6.5 * PT;
  const codeSize = 17 * PT;
  setFont(ctx, labelSize);
  setSpacing(ctx, 0.14, labelSize);
  const label = texts.code.toUpperCase();
  const labelW = ctx.measureText(label).width;
  setFont(ctx, codeSize, '700', MONO);
  setSpacing(ctx, 0.08, codeSize);
  const code = spaced(card.code);
  const codeW = ctx.measureText(code).width;
  const boxH = 1.5 + labelSize * 1.2 + codeSize * 1.1 + 1.5;
  const boxTop = registerTop - 2 - boxH;
  const boxW = px(3 * 2) + Math.max(labelW, codeW);
  roundRect(ctx, px(left), px(boxTop), boxW, px(boxH), px(2));
  ctx.fillStyle = '#eef8fe';
  ctx.fill();
  ctx.strokeStyle = '#1fa8ea';
  ctx.lineWidth = px(0.4);
  ctx.stroke();
  setFont(ctx, labelSize);
  setSpacing(ctx, 0.14, labelSize);
  ctx.fillStyle = '#4b5874';
  ctx.fillText(label, px(left + 3), px(boxTop + 1.5));
  setFont(ctx, codeSize, '700', MONO);
  setSpacing(ctx, 0.08, codeSize);
  ctx.fillStyle = '#0d1220';
  ctx.fillText(code, px(left + 3), px(boxTop + 1.5 + labelSize * 1.2 + codeSize * 0.05));
  setSpacing(ctx, 0, codeSize);

  if (style.withQr) drawQr(ctx, cardUrl(card), ox + CARD_W - 5 - 27, oy + CARD_H - 4 - 27, 27);
  ctx.restore();
}

/** Cards in a grid of `cols` columns; the canvas is only as tall as the rows in use. */
function drawSheet(logo: HTMLImageElement, cards: InviteCard[], firstIndex: number, cols: number, margin: number, style: CardStyle) {
  const rows = Math.ceil(cards.length / cols);
  const widthMm = cols * CARD_W + 2 * margin;
  const heightMm = rows * CARD_H + 2 * margin;
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(px(widthMm));
  canvas.height = Math.round(px(heightMm));
  const ctx = canvas.getContext('2d')!;
  ctx.fillStyle = '#fff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  cards.forEach((card, i) => {
    drawCard(ctx, logo, card, firstIndex + i, margin + (i % cols) * CARD_W, margin + Math.floor(i / cols) * CARD_H, style);
  });
  return { canvas, widthMm, heightMm };
}

function jpegBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('jpeg'))), 'image/jpeg', 0.92));
}

function download(blob: Blob, name: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

const pause = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/** "Erä 3 / Mikko" → "era-3-mikko" for file names. */
export function fileSlug(label: string): string {
  return (
    label
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '') || 'kutsukoodit'
  );
}

/** Number of files the format produces. */
export function fileCount(format: SaveFormat, cards: number): number {
  if (format === 'pdf') return cards > 0 ? 1 : 0;
  if (format === 'jpg-sheets') return Math.ceil(cards / CARDS_PER_PAGE);
  return cards;
}

/** The files of the chosen format, one at a time (a large batch is not kept in memory at once). */
export async function* inviteCardFiles(
  cards: InviteCard[],
  withQr: boolean,
  format: SaveFormat,
  batchLabel: string,
): AsyncGenerator<{ name: string; blob: Blob }> {
  if (cards.length === 0) return;
  await document.fonts?.ready;
  const logo = await logoImage();
  const kind = cards[0]!.kind;
  const base = `areena-${kind === 'guest' ? 'vieraskortit' : kind === 'bingo' ? 'bingokoodit' : 'kutsukortit'}-${fileSlug(batchLabel)}`;
  const sheets: InviteCard[][] = [];
  for (let i = 0; i < cards.length; i += CARDS_PER_PAGE) sheets.push(cards.slice(i, i + CARDS_PER_PAGE));

  if (format === 'txt-zip') {
    const encoder = new TextEncoder();
    const bom = String.fromCharCode(0xfeff); // Notepad and phones recognise the text as UTF-8
    const texts = t.admin.bingo;
    const entries = cards.map((card) => ({
      name: `areena-bingo-${card.code}.txt`,
      data: encoder.encode(bom + texts.textFile(card.code, spaced(card.code), cardSite(card.kind), cardUrl(card)) + '\r\n'),
    }));
    entries.push({
      name: texts.textListName,
      data: encoder.encode(bom + texts.textList(batchLabel, cardSite(kind), cards.map((c) => spaced(c.code))) + '\r\n'),
    });
    const zip = buildZip(entries);
    yield { name: `${base}.zip`, blob: new Blob([zip.buffer as ArrayBuffer], { type: 'application/zip' }) };
    return;
  }

  if (format === 'pdf') {
    // A4 pages like the printout; a partial last page has only the rows in use.
    const pages: PdfImage[] = [];
    for (const [s, sheet] of sheets.entries()) {
      const { canvas, widthMm, heightMm } = drawSheet(logo, sheet, s * CARDS_PER_PAGE, 2, 0, { withQr, frame: 'cut' });
      const jpeg = new Uint8Array(await (await jpegBlob(canvas)).arrayBuffer());
      pages.push({ jpeg, pixelWidth: canvas.width, pixelHeight: canvas.height, x: PAGE_MARGIN, y: PAGE_MARGIN, width: widthMm, height: heightMm });
      canvas.width = canvas.height = 0; // frees the memory right away (iOS)
    }
    const pdf = buildPdf(pages);
    yield { name: `${base}.pdf`, blob: new Blob([pdf.buffer as ArrayBuffer], { type: 'application/pdf' }) };
    return;
  }

  if (format === 'jpg-sheets') {
    for (const [s, sheet] of sheets.entries()) {
      // A single card is not padded to two columns.
      const { canvas } = drawSheet(logo, sheet, s * CARDS_PER_PAGE, sheet.length === 1 ? 1 : 2, JPG_MARGIN, { withQr, frame: 'cut' });
      yield { name: sheets.length === 1 ? `${base}.jpg` : `${base}-${s + 1}.jpg`, blob: await jpegBlob(canvas) };
      canvas.width = canvas.height = 0;
    }
    return;
  }

  // One image per card, e.g. for sending one code to one person.
  for (const [i, card] of cards.entries()) {
    const { canvas } = drawSheet(logo, [card], i, 1, 2, { withQr, frame: 'thin' });
    yield { name: `areena-${kind === 'guest' ? 'vieras' : kind === 'bingo' ? 'bingo' : 'kutsu'}-${card.code}.jpg`, blob: await jpegBlob(canvas) };
    canvas.width = canvas.height = 0;
  }
}

/** Saves the cards as files on the computer (the browser's downloads). */
export async function saveInviteCards(cards: InviteCard[], withQr: boolean, format: SaveFormat, batchLabel: string): Promise<void> {
  let first = true;
  for await (const file of inviteCardFiles(cards, withQr, format, batchLabel)) {
    if (!first) await pause(400); // browsers drop downloads that come too fast
    first = false;
    download(file.blob, file.name);
  }
}
